import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { MessageSquare, Save, Send, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { api } from '../../utils/api';

/**
 * Where an admin points acting-ride announcements at a Discord channel.
 *
 * The stored webhook URL is never sent to the browser — the server returns a
 * masked hint instead. So the input starts empty and blank means "keep the one
 * you already have"; only typing a new URL replaces it.
 */
const DiscordSettings = () => {
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [botName, setBotName] = useState('');
  const [showUrl, setShowUrl] = useState(false);

  const { data, isLoading } = useQuery(
    'discordSettings',
    () => api.get('/discord-settings'),
    {
      select: (res) => res?.data?.data,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!data) return;
    setEnabled(Boolean(data.enabled));
    setBotName(data.botName || '');
  }, [data]);

  const saveMutation = useMutation(
    (body) => api.put('/discord-settings', body),
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message || 'Discord settings saved');
        setWebhookUrl('');
        queryClient.invalidateQueries('discordSettings');
      },
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to save settings'),
    },
  );

  const testMutation = useMutation(
    () => api.post('/discord-settings/test', webhookUrl.trim() ? { webhookUrl: webhookUrl.trim() } : {}),
    {
      onSuccess: (res) => toast.success(res?.data?.message || 'Test message sent'),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to send test message'),
    },
  );

  const handleSave = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      enabled,
      webhookUrl: webhookUrl.trim(),
      botName: botName.trim(),
    });
  };

  const hasWebhook = Boolean(data?.hasWebhook);
  const configured = Boolean(data?.configured);
  const canTest = hasWebhook || Boolean(webhookUrl.trim());

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-6 w-6 text-primary-600" />
            <div>
              <h1 className="page-title">Discord Notifications</h1>
              <p className="page-subtitle">
                Announce newly booked acting rides in a Discord channel
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Live status */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex h-3 w-3 rounded-full ${configured ? 'bg-green-500' : 'bg-gray-300'}`}
              />
              <div>
                <p className="font-medium text-gray-900">
                  {configured ? 'Announcements are on' : 'Announcements are off'}
                </p>
                <p className="text-xs text-gray-500">
                  {configured
                    ? 'New acting ride bookings are posted to your channel.'
                    : hasWebhook
                      ? 'A webhook is saved, but notifications are disabled.'
                      : 'Add a webhook URL to get started.'}
                </p>
              </div>
            </div>
            {data?.updatedAt && (
              <p className="text-xs text-gray-400">
                Last changed by {data.updatedBy || 'unknown'} on{' '}
                {new Date(data.updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          {data?.source === 'env' && hasWebhook && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Currently using the webhook from the server's <code>.env</code> file.
              Saving here stores it in the database instead and takes precedence.
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSave} className="card">
        <div className="card-header">
          <h3 className="card-title">Webhook</h3>
        </div>
        <div className="card-body space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook URL
            </label>
            <div className="flex gap-2">
              <input
                type={showUrl ? 'text' : 'password'}
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder={hasWebhook ? data.webhookMasked || 'Saved — leave blank to keep' : 'https://discord.com/api/webhooks/...'}
                autoComplete="off"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowUrl((v) => !v)}
                className="btn btn-outline px-3"
                title={showUrl ? 'Hide' : 'Show'}
              >
                {showUrl ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {hasWebhook
                ? 'A webhook is already saved. Leave this blank to keep it, or paste a new one to replace it.'
                : 'Discord → Server Settings → Integrations → Webhooks → New Webhook → Copy Webhook URL.'}
            </p>
            <a
              href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline mt-1"
            >
              How to create a Discord webhook <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Post as
            </label>
            <input
              type="text"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder="Acting Rides"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">The name messages appear under in the channel.</p>
          </div>

          <div
            onClick={() => setEnabled((v) => !v)}
            className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer select-none transition-all
              ${enabled ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          >
            <div>
              <p className="font-medium text-gray-800">Enable announcements</p>
              <p className="text-xs text-gray-500">
                Post a message whenever a new acting ride is booked.
              </p>
            </div>
            <div className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${enabled ? 'right-1' : 'left-1'}`} />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={!canTest || testMutation.isLoading}
              className="btn btn-outline flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {testMutation.isLoading ? 'Sending...' : 'Send test message'}
            </button>
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="btn btn-primary flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isLoading ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </div>
      </form>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">What gets posted</h3>
        </div>
        <div className="card-body">
          <p className="text-sm text-gray-600 mb-3">
            One message per new acting ride booking, containing:
          </p>
          <div className="flex flex-wrap gap-2">
            {['Ride ID', 'Status', 'Booked at', 'Scheduled window', 'Duration', 'Rider name & phone', 'Fare range', 'Pickup', 'Drop', 'Customer vehicle'].map((f) => (
              <span key={f} className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700">
                {f}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Only bookings made after the server last started are announced — switching this on
            will not post your existing trips.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DiscordSettings;
